import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Mail, User as UserIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { GoogleContinue, ValidatedInput } from '../../../components/design-system/auth';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useGoogleClientId } from '../../../common/hooks/use-google-client-id.hook';
import AuthClientStore from '../../../common/services/auth-client.service';
import { InvitationPreview, useAccountApi } from '../../../common/hooks/use-account-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { isStrongPassword } from '../../../common/utils/password-policy';
import { AccountHeading, AccountNotice, AccountPage, isValidEmail, PasswordField } from './components/AccountPage';

const INVITATION_ERRORS = ['INVITATION_NOT_FOUND', 'INVITATION_EXPIRED', 'INVITATION_REVOKED', 'INVITATION_USED'];

type Phase =
    | { kind: 'loading' }
    | { kind: 'invalid'; code: string }
    | { kind: 'form'; invitation: InvitationPreview }
    | { kind: 'done'; email: string; verified: boolean };

/**
 * Where an invitation link lands (/invite/:token). The person picks their own
 * password; from a shared link they also give their email, which they then
 * confirm before they can sign in.
 */
const AcceptInvitationPage = () => {
    const { t } = useTranslation();
    const { token = '' } = useParams();
    const navigate = useNavigate();
    const accountApi = useAccountApi();
    const { me, setGlobalLoading } = useAuthContext();
    const googleClientId = useGoogleClientId();

    const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
    const [email, setEmail] = useState('');
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [password, setPassword] = useState('');
    const [check, setCheck] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let live = true;
        accountApi
            .previewInvitation(token)
            .then((invitation) => live && setPhase({ kind: 'form', invitation }))
            .catch(async (e) => {
                const { status, code } = await readApiError(e);
                const reason = status === 429 ? 'TOO_MANY' : INVITATION_ERRORS.includes(code) ? code : 'INVITATION_NOT_FOUND';
                if (live) setPhase({ kind: 'invalid', code: reason });
            });
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const invitation = phase.kind === 'form' ? phase.invitation : null;
    const needsEmail = invitation?.delivery === 'link';
    const nameOk = firstname.trim() !== '' && lastname.trim() !== '';
    const emailOk = !needsEmail || isValidEmail(email);
    const passwordOk = isStrongPassword(password);

    const submit = async () => {
        setCheck(true);
        setError(null);
        if (!nameOk || !emailOk || !passwordOk || submitting) return;
        setSubmitting(true);
        try {
            const res = await accountApi.acceptInvitation({
                token,
                firstname: firstname.trim(),
                lastname: lastname.trim(),
                password,
                ...(needsEmail && { email: email.trim() }),
            });
            setPhase({ kind: 'done', email: res.email, verified: res.verified });
        } catch (e) {
            const { status, code } = await readApiError(e);
            if (INVITATION_ERRORS.includes(code)) setPhase({ kind: 'invalid', code });
            else if (code === 'EMAIL_TAKEN' || code === 'NO_SEATS_LEFT') setError(t(`ACCOUNT.INVITE.ERRORS.${code}`));
            else if (status === 429) setError(t('ACCOUNT.COMMON.TOO_MANY'));
            else setError(t('ACCOUNT.COMMON.GENERIC_ERROR'));
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Join with Google: the API checks the Google address against the invitation
     * and signs the new account straight in, as the login page leaves it.
     */
    const joinWithGoogle = async (credential: string) => {
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            const res = await accountApi.acceptInvitationWithGoogle({ token, credential });
            AuthClientStore.setAccessToken(res.access_token);
            AuthClientStore.setRefreshToken(res.refresh_token);
            setGlobalLoading(true, t('AUTH.LOADING.WORKSPACE'));
            try {
                await me();
            } catch (e) {
                setGlobalLoading(false);
                throw e;
            }
            navigate('/dashboard', { replace: true });
        } catch (e) {
            const { status, code } = await readApiError(e);
            if (INVITATION_ERRORS.includes(code)) setPhase({ kind: 'invalid', code });
            else if (code === 'GOOGLE_EMAIL_MISMATCH') setError(t('ACCOUNT.INVITE.ERRORS.GOOGLE_EMAIL_MISMATCH', { email: invitation?.email }));
            else if (code === 'GOOGLE_EMAIL_UNVERIFIED' || code === 'EMAIL_TAKEN' || code === 'NO_SEATS_LEFT') setError(t(`ACCOUNT.INVITE.ERRORS.${code}`));
            else if (status === 429) setError(t('ACCOUNT.COMMON.TOO_MANY'));
            else setError(t('ACCOUNT.INVITE.ERRORS.GOOGLE_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    const onEnter = (e: any) => {
        if (e.key === 'Enter') submit();
    };

    if (phase.kind === 'loading') {
        return (
            <AccountPage>
                <p className="mb-4 text-center text-sm text-muted-foreground" role="status">{t('ACCOUNT.INVITE.LOADING')}</p>
                <Skeleton className="h-64 rounded-xl" />
            </AccountPage>
        );
    }

    if (phase.kind === 'invalid') {
        return (
            <AccountPage>
                <AccountHeading title={t('ACCOUNT.INVITE.TITLE_PLATFORM')} />
                <AccountNotice tone="error" testId="invitation-invalid">
                    {phase.code === 'TOO_MANY' ? t('ACCOUNT.COMMON.TOO_MANY') : t(`ACCOUNT.INVITE.ERRORS.${phase.code}`)}
                </AccountNotice>
                <Button variant="outline" className="h-11 w-full" onClick={() => navigate('/login')}>
                    {t('ACCOUNT.COMMON.BACK_TO_LOGIN')}
                </Button>
            </AccountPage>
        );
    }

    if (phase.kind === 'done') {
        return (
            <AccountPage>
                {phase.verified ? (
                    <AccountNotice tone="success" title={t('ACCOUNT.INVITE.DONE_TITLE')} testId="invitation-done">
                        {t('ACCOUNT.INVITE.DONE')}
                    </AccountNotice>
                ) : (
                    <AccountNotice tone="info" title={t('ACCOUNT.INVITE.CHECK_EMAIL_TITLE')} testId="invitation-check-email">
                        {t('ACCOUNT.INVITE.CHECK_EMAIL', { email: phase.email })}
                    </AccountNotice>
                )}
                <Button className="h-11 w-full" onClick={() => navigate('/login')}>
                    {t('ACCOUNT.INVITE.GO_TO_LOGIN')}
                </Button>
            </AccountPage>
        );
    }

    return (
        <AccountPage>
            <AccountHeading
                title={invitation.vendorName ? t('ACCOUNT.INVITE.TITLE', { vendor: invitation.vendorName }) : t('ACCOUNT.INVITE.TITLE_PLATFORM')}
                subtitle={needsEmail ? t('ACCOUNT.INVITE.SUBTITLE_LINK') : t('ACCOUNT.INVITE.SUBTITLE_EMAIL', { email: invitation.email })}
            />

            {error && <AccountNotice tone="error" testId="invitation-error">{error}</AccountNotice>}

            <GoogleContinue
                clientId={googleClientId}
                onCredential={joinWithGoogle}
                onError={() => setError(t('ACCOUNT.INVITE.ERRORS.GOOGLE_FAILED'))}
                context="signup"
                testId="invitation-google"
            />

            <div className="flex flex-col gap-5">
                {needsEmail && (
                    <ValidatedInput
                        label={`${t('ACCOUNT.COMMON.EMAIL_LABEL')}*`}
                        placeholder={t('ACCOUNT.COMMON.EMAIL_PLACEHOLDER')}
                        value={email}
                        onChange={setEmail}
                        onKeyDown={onEnter}
                        type="email"
                        autoComplete="email"
                        error={check && !emailOk}
                        errorMessage={t('ACCOUNT.COMMON.EMAIL_INVALID')}
                        startIcon={<Mail />}
                    />
                )}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <ValidatedInput
                        label={`${t('ACCOUNT.INVITE.FIRST_NAME')}*`}
                        placeholder=""
                        value={firstname}
                        onChange={setFirstname}
                        onKeyDown={onEnter}
                        autoComplete="given-name"
                        error={check && !firstname.trim()}
                        startIcon={<UserIcon />}
                    />
                    <ValidatedInput
                        label={`${t('ACCOUNT.INVITE.LAST_NAME')}*`}
                        placeholder=""
                        value={lastname}
                        onChange={setLastname}
                        onKeyDown={onEnter}
                        autoComplete="family-name"
                        error={check && !lastname.trim()}
                    />
                </div>
                {check && !nameOk && <p className="-mt-3 text-xs text-destructive">{t('ACCOUNT.INVITE.NAME_REQUIRED')}</p>}
                <PasswordField
                    label={`${t('ACCOUNT.COMMON.PASSWORD_LABEL')}*`}
                    value={password}
                    onChange={setPassword}
                    onKeyDown={onEnter}
                    error={check && !passwordOk}
                    errorMessage={t('ACCOUNT.COMMON.PASSWORD_WEAK')}
                    showRules
                />
            </div>

            <Button className="mt-6 h-11 w-full" onClick={submit} disabled={submitting}>
                {t('ACCOUNT.INVITE.SUBMIT')}
            </Button>
        </AccountPage>
    );
};

export default AcceptInvitationPage;
